import { Suspense, useEffect, useRef, useState, lazy } from 'react';
// @mui
import { styled } from '@mui/material/styles';
import {
  Stack,
  Input,
  Divider,
  IconButton,
  InputAdornment,
  Box,
  FormHelperText,
  Tooltip,
} from '@mui/material';
// components
import Iconify from 'src/minimals/components/Iconify';
import { useTranslation } from 'react-i18next';
import useAudioRecorder from 'src/hooks/useAudioRecorder';
import { secondsToHms } from 'src/utilities/formatTime';
import { useSelector } from 'react-redux';

const EmojiPicker = lazy(() => import('./EmojiPicker/EmojiPicker'));

export default function ChatMessageInput({ disabled, onSend }) {
  const { activeConversationId } = useSelector((state) => state.chat);
  const fileInputRef = useRef(null);
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const isValidMessage = message.trim().length <= 255;
  const isAudioMp4Supported = MediaRecorder.isTypeSupported('audio/mp4');
  const {
    audio,
    recordingAudioDuration,
    isRecording,
    recordAudio,
    stopRecording,
    clearRecording,
  } = useAudioRecorder();

  useEffect(() => {
    if (audio) {
      onSend({
        message: audio.data,
        type: 'audio',
        contentType: 'audio/mp4',
        attachments: [],
        createdAt: new Date(),
      });
    }
  }, [audio]);

  useEffect(() => {
    setMessage('');
    if (isRecording) {
      clearRecording();
    }
  }, [activeConversationId]);

  const handleAttach = () => {
    fileInputRef.current?.click();
  };

  const handleKeyUp = (event) => {
    if (event.key === 'Enter') {
      handleSend();
    }
  };

  const handleSend = () => {
    if (!message || !isValidMessage) return '';

    if (message.trim().length === 0) return setMessage('');

    if (onSend) {
      onSend({
        message: message.trim(),
        type: 'text',
        attachments: [],
        createdAt: new Date(),
      });
    }
    return setMessage('');
  };

  return (
    <RootStyle>
      <Box
        flexGrow={1}
        position="relative"
        display="flex"
        justifyContent="flex-end"
      >
        {!isRecording ? (
          <>
            <Input
              disabled={disabled}
              fullWidth
              error={!isValidMessage}
              value={message}
              disableUnderline={isValidMessage}
              onKeyUp={handleKeyUp}
              onChange={(event) => setMessage(event.target.value)}
              placeholder={t('chat.input_text_placeholder')}
              startAdornment={
                <InputAdornment position="start">
                  <Suspense fallback={<div>Loading...</div>}>
                    <EmojiPicker
                      disabled={disabled}
                      value={message}
                      setValue={setMessage}
                    />
                  </Suspense>
                </InputAdornment>
              }
              endAdornment={
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ flexShrink: 0, mr: 1.5 }}
                >
                  {/* <IconButton
                    disabled={disabled}
                    size="small"
                    onClick={handleAttach}
                  >
                    <Iconify
                      icon="ic:round-add-photo-alternate"
                      width={22}
                      height={22}
                    />
                  </IconButton>
                  <IconButton
                    disabled={disabled}
                    size="small"
                    onClick={handleAttach}
                  >
                    <Iconify icon="eva:attach-2-fill" width={22} height={22} />
                  </IconButton> */}
                  <Tooltip
                    title={
                      isAudioMp4Supported
                        ? t('chat.record_audio_button_text')
                        : t('chat.record_audio_not_supported_button_text')
                    }
                    placement="top"
                  >
                    <span>
                      <IconButton
                        disabled={disabled || !isAudioMp4Supported}
                        size="small"
                        onClick={recordAudio}
                      >
                        <Iconify icon="eva:mic-fill" width={22} height={22} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              }
            />
            {!isValidMessage && (
              <FormHelperText
                error
                sx={{
                  position: 'absolute',
                  top: -26,
                  left: '50%',
                  transform: 'translateX(-50%)',
                }}
              >
                {t('chat.input_text_error')}
              </FormHelperText>
            )}
          </>
        ) : (
          <>
            <Box
              component="span"
              sx={{
                p: '5px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {secondsToHms(recordingAudioDuration)}
            </Box>
            <IconButton
              disabled={disabled}
              size="small"
              onClick={stopRecording}
              sx={{
                marginRight: '12px',
              }}
            >
              <Iconify
                icon="eva:mic-off-fill"
                color="crimson"
                width={22}
                height={22}
              />
            </IconButton>
          </>
        )}
      </Box>
      <Divider orientation="vertical" flexItem />
      <IconButton
        color="primary"
        disabled={!message || !isValidMessage}
        onClick={handleSend}
        sx={{ mx: 1 }}
      >
        <Iconify icon="ic:round-send" width={22} height={22} />
      </IconButton>
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} />
    </RootStyle>
  );
}

const RootStyle = styled('div')(({ theme }) => ({
  minHeight: 56,
  display: 'flex',
  position: 'relative',
  alignItems: 'center',
  paddingLeft: theme.spacing(2),
}));
